import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithAuth } from "@store/middleware";

import { applicationUrl } from "@constants";
import { type IDocument, type ICourt, type IAttachment } from "@types";
import { userDocumentsApi } from "@store/services/userDocuments";
import { type TProductType } from "@shared/types/product.types";
import { type TRegistrationType, type TSignatureType } from "@shared/types/document.types";

export const DOCUMENTS_API_REDUCER_KEY = "documentsApi";
export const documentsApi = createApi({
  reducerPath: DOCUMENTS_API_REDUCER_KEY,
  baseQuery: baseQueryWithAuth({
    baseUrl: applicationUrl,
  }),
  endpoints: (builder) => ({
    getDocument: builder.query<IDocument, IDocument["id"]>({
      query: (id) => ({
        url: `/api/document/${id}`,
        method: "GET",
      }),
    }),
    getDocumentGenericData: builder.query<IDocument, TProductType>({
      query: (productType) => ({
        url: `/api/documents/type/${productType}`,
        method: "GET",
      }),
    }),
    deleteDocument: builder.mutation<void, IDocument["id"]>({
      query: (id) => ({
        url: `/api/document/${id}`,
        method: "DELETE",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(userDocumentsApi.util.invalidateTags(["USER_DOCUMENTS"]));
        } catch {
          console.error("Ops, Failed to delete document");
        }
      },
    }),
    uploadDocument: builder.mutation<void, FormData>({
      query: (document) => ({
        url: "/api/documents/upload",
        method: "POST",
        body: document,
        responseHandler: "text",
      }),
      onQueryStarted: async (_, { dispatch, queryFulfilled }) => {
        try {
          await queryFulfilled;
          dispatch(userDocumentsApi.util.invalidateTags(["USER_DOCUMENTS"]));
        } catch {
          console.error("Ops, Failed to upload document");
        }
      },
    }),
    getAttachments: builder.query<IAttachment[], void>({
      query: () => ({
        url: "/api/attachments",
        method: "GET",
      }),
    }),
    confirmDocument: builder.mutation<void, { id: IDocument["id"] }>({
      query: ({ id }) => ({
        url: `/api/document/${id}/confirm`,
        method: "PUT",
      }),
    }),
    getCourts: builder.query<ICourt[], void>({
      query: () => ({
        url: "/api/documents/registration/courts",
        method: "GET",
      }),
    }),
    postCourtRegistration: builder.mutation<
      void,
      {
        id: IDocument["id"];
        court: string | undefined | null;
        type: TRegistrationType | undefined | null;
      }
    >({
      query: (data) => ({
        url: `/api/document/${data.id}/registration`,
        method: "POST",
        body: data,
      }),
    }),
    postSignatureMethod: builder.mutation<
      void,
      {
        id: IDocument["id"];
        signatureType: TSignatureType;
      }
    >({
      query: (data) => ({
        url: `/api/document/${data.id}/select-signature`,
        method: "POST",
        body: data,
      }),
    }),
    postSign: builder.mutation<{ redirect_url: string }, IDocument["id"]>({
      query: (id) => ({
        url: `/api/documents/sign-redirect/${id}`,
        method: "POST",
      }),
    }),
    postEditDocument: builder.mutation<
      {
        product: IDocument["type"];
      },
      IDocument["id"]
    >({
      query: (id) => ({
        url: `/api/documents/${id}/edit`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useDeleteDocumentMutation,
  useUploadDocumentMutation,
  useGetAttachmentsQuery,
  useConfirmDocumentMutation,
  useLazyGetDocumentQuery,
  useGetDocumentQuery,
  useGetDocumentGenericDataQuery,
  useLazyGetCourtsQuery,
  useGetCourtsQuery,
  usePostCourtRegistrationMutation,
  usePostSignatureMethodMutation,
  usePostSignMutation,
  usePostEditDocumentMutation,
} = documentsApi;
